import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  Button,
  Alert,
  Skeleton,
} from '@mui/material';
import {
  CheckCircle,
  Error,
  Warning,
  Refresh,
  Payment,
  Info,
} from '@mui/icons-material';
import { subscriptionApi } from '../services/api';
import { useNotification } from '../hooks/useNotification';
import UsageMeter from './UsageMeter';
import PaymentButton from './PaymentButton';

/**
 * Subscription Status Component
 * Displays current subscription status, usage, and renewal options
 */
const SubscriptionStatus = ({ onRenewClick, onPaymentSuccess, onPaymentError }) => {
  const { showError } = useNotification();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    loadSubscriptionStatus();
  }, []);

  const loadSubscriptionStatus = async () => {
    try {
      setLoading(true);
      const response = await subscriptionApi.getStatus();
      setSubscription(response.data);
    } catch (error) {
      console.error('Error loading subscription:', error);
      showError('Failed to load subscription status');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Skeleton variant="text" width="60%" height={32} />
            <Skeleton variant="rectangular" height={100} />
            <Skeleton variant="text" width="40%" />
          </Stack>
        </CardContent>
      </Card>
    );
  }

  if (!subscription) {
    return (
      <Card>
        <CardContent>
          <Alert severity="warning">
            Unable to load subscription information. Please try again later.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const {
    status,
    isActive,
    subscriptionType,
    daysRemaining,
    needsRenewal,
    limitExceeded,
    subscriptionEndDate,
  } = subscription;

  // Determine status display
  const getStatusChip = () => {
    if (status === 'whitelisted') {
      return (
        <Chip
          icon={<CheckCircle />}
          label="Whitelisted - Unlimited"
          color="success"
          variant="filled"
        />
      );
    }

    if (!isActive) {
      return (
        <Chip icon={<Error />} label="Expired" color="error" variant="filled" />
      );
    }

    if (status === 'trial') {
      return (
        <Chip
          icon={<Info />}
          label={`Trial - ${daysRemaining} days left`}
          color="info"
          variant="filled"
        />
      );
    }

    if (status === 'active') {
      return (
        <Chip
          icon={<CheckCircle />}
          label="Active"
          color="success"
          variant="filled"
        />
      );
    }

    return <Chip label={status} color="default" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <Stack spacing={2}>
      {/* Subscription Status Card */}
      <Card>
        <CardContent>
          <Stack spacing={2}>
            {/* Header */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6">Subscription Status</Typography>
              <Button
                size="small"
                startIcon={<Refresh />}
                onClick={loadSubscriptionStatus}
              >
                Refresh
              </Button>
            </Stack>

            {/* Status Chip */}
            <Box>{getStatusChip()}</Box>

            {/* Subscription Details */}
            <Stack spacing={1}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Plan Type
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {subscriptionType}
                </Typography>
              </Box>

              {subscriptionEndDate && status !== 'whitelisted' && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {status === 'trial' ? 'Trial Ends' : 'Renews On'}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formatDate(subscriptionEndDate)}
                    {daysRemaining !== null && (
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                        sx={{ ml: 1 }}
                      >
                        ({daysRemaining} days)
                      </Typography>
                    )}
                  </Typography>
                </Box>
              )}
            </Stack>

            {/* Alerts */}
            {!isActive && (
              <Alert severity="error" icon={<Error />}>
                <Typography variant="body2" fontWeight="medium">
                  Your subscription has expired
                </Typography>
                <Typography variant="body2">
                  Renew now to continue uploading and processing files.
                </Typography>
              </Alert>
            )}

            {needsRenewal && isActive && (
              <Alert severity="warning" icon={<Warning />}>
                <Typography variant="body2">
                  Your subscription expires in {daysRemaining} days. Renew soon
                  to avoid interruption.
                </Typography>
              </Alert>
            )}

            {limitExceeded && (
              <Alert severity="error" icon={<Error />}>
                <Typography variant="body2" fontWeight="medium">
                  You've reached your monthly page limit
                </Typography>
                <Typography variant="body2">
                  Upgrade or wait for your monthly reset to continue.
                </Typography>
              </Alert>
            )}

            {/* Action Buttons */}
            {status !== 'whitelisted' && (!isActive || needsRenewal || limitExceeded) && (
              <PaymentButton
                onSuccess={(data) => {
                  loadSubscriptionStatus();
                  if (onPaymentSuccess) onPaymentSuccess(data);
                }}
                onError={onPaymentError}
                fullWidth
                size="large"
              >
                {!isActive ? 'Renew Subscription' : 'Upgrade Now'}
              </PaymentButton>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Usage Meter */}
      <UsageMeter subscription={subscription} />
    </Stack>
  );
};

export default SubscriptionStatus;

